import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import NextApp from 'next/app';
import getConfig from 'next/config';
import { getToken } from '@grandvisionhq/auth';
import packageInfo from '@grandvisionhq/auth/package.json';
import { initApolloClient, ApolloProvider } from '@grandvisionhq/graphql';

// We read the core version from the package dependencies of the auth package,
// which is injected at opco build time.
const CORE_VERSION = packageInfo.dependencies['@grandvisionhq/common'];
const { publicRuntimeConfig  } = getConfig();
const { grandvision  } = publicRuntimeConfig;
const graphQLUrl = grandvision.endpoints.graphql;
const clientInfo = publicRuntimeConfig.apolloClientInfo || {};
// this is its own function to allow effective testing of the feature toggle
const getIsPersistentQueryDisabled = ()=>{
    const { features =[]  } = getConfig().publicRuntimeConfig.grandvision;
    return features.includes('PERSISTENT_QUERY_DISABLED');
};
const getDisplayName = (Component)=>Component.displayName || Component.name || 'Unknown'
;
const withApollo = ()=>{
    return (PageComponent)=>{
        const WithApollo = (_param)=>{
            var { apolloClient , apolloState  } = _param, pageProps = swcHelpers.objectWithoutProperties(_param, [
                "apolloClient",
                "apolloState"
            ]);
            // Enrich the received versions with core version
            const versions = swcHelpers.objectSpread({}, clientInfo.versions, {
                core: CORE_VERSION
            });
            const client = apolloClient || initApolloClient(graphQLUrl, {
                initialState: apolloState,
                getToken,
                queryOptions: {
                    isPersistentQueryDisabled: getIsPersistentQueryDisabled()
                },
                clientInfo: swcHelpers.objectSpread({}, clientInfo, {
                    versions
                })
            });
            return /*#__PURE__*/ React.createElement(ApolloProvider, {
                client: client
            }, /*#__PURE__*/ React.createElement(PageComponent, swcHelpers.extends({}, pageProps)));
        };
        if (PageComponent.getInitialProps) {
            WithApollo.getInitialProps = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(ctx) {
                    const inAppContext = Boolean(ctx === null || ctx === void 0 ? void 0 : ctx.ctx);
                    if (ctx.apolloClient) {
                        console.error('Multiple instances of withApollo found.');
                        throw new Error('Multiple instances of withApollo found.');
                    }
                    const apolloClient = initApolloClient(graphQLUrl, {
                        getToken,
                        queryOptions: {
                            isPersistentQueryDisabled: getIsPersistentQueryDisabled()
                        }
                    });
                    // Add apolloClient to NextPageContext & NextAppContext
                    // This allows us to consume the apolloClient inside our
                    // custom `getInitialProps({ apolloClient })`.
                    ctx.apolloClient = apolloClient;
                    if (inAppContext) {
                        ctx.ctx.apolloClient = apolloClient;
                    }
                    // Run wrapped getInitialProps methods
                    let pageProps = {};
                    if (PageComponent.getInitialProps) {
                        pageProps = yield PageComponent.getInitialProps(ctx);
                    } else if (inAppContext) {
                        pageProps = yield NextApp.getInitialProps(ctx);
                    }
                    if (typeof window === 'undefined') {
                        // When redirecting, the response is finished.
                        // No point in continuing to render
                        if (ctx.res && (ctx.res.headersSent || ctx.res.finished)) {
                            return pageProps;
                        }
                    }
                    // Extract query data from the Apollo store
                    const apolloState = apolloClient.cache.extract();
                    return swcHelpers.objectSpread({}, pageProps, {
                        apolloState
                    });
                });
                return function(ctx) {
                    return _ref.apply(this, arguments);
                };
            }();
        }
        WithApollo.displayName = `WithApollo(${getDisplayName(PageComponent)})`;
        return WithApollo;
    };
};

export { withApollo as default };
//# sourceMappingURL=with-apollo.js.map
